<template>
  <Menu
    :model="model"
    class="bg-transparent border-none p-0"
    :pt="{
      submenuLabel: 'p-0 pl-4 pt-4 text-surface-400 font-semibold text-sm',
    }"
  >
    <template #item="{ item, props }">
      <NuxtLink
        v-if="item.url"
        class="flex w-full min-w-0 items-center gap-2 cursor-pointer px-4 py-2"
        :to="item.url"
        v-bind="props.action"
      >
        <div class="flex shrink-0 items-center justify-center w-5">
          <span class="text-surface-500" :class="item.icon" />
        </div>
        <span class="min-w-0 flex-1 text-surface-700">
          {{ item.label }}
        </span>
        <Badge
          v-if="itemBadge(item)"
          :value="itemBadge(item)!"
          severity="danger"
          class="shrink-0 min-w-6 justify-center px-1.5 text-xs font-bold leading-none"
        />
      </NuxtLink>
      <span
        v-else
        class="flex w-full min-w-0 items-center gap-2 cursor-pointer px-4 py-2"
        v-bind="props.action"
      >
        <div class="flex shrink-0 items-center justify-center w-5">
          <span class="text-surface-500" :class="item.icon" />
        </div>
        <span class="min-w-0 flex-1 text-surface-700">
          {{ item.label }}
        </span>
        <Badge
          v-if="itemBadge(item)"
          :value="itemBadge(item)!"
          severity="danger"
          class="shrink-0 min-w-6 justify-center px-1.5 text-xs font-bold leading-none"
        />
      </span>
    </template>
  </Menu>
</template>

<script setup lang="ts">
  import NuxtLink from "#app/components/nuxt-link";
  import Badge from "primevue/badge";
  import Menu, { type MenuProps } from "primevue/menu";
  import type { MenuItem } from "primevue/menuitem";
  import type { AuthRequirement } from "~/layers/auth/types";

  const auth = useAuth();

  const user = computed(() => auth.data.value?.user);
  const viewer = computed(() => ({
    isAuthenticated: auth.status.value === "authenticated",
    grants: user.value?.role.grants ?? {},
  }));

  export type SidebarMenuItem = MenuItem & {
    auth?: AuthRequirement;
    /** Unread count badge (e.g. inbox); shown when &gt; 0. */
    badge?: number;
  };

  export interface SidebarMenuProps
    extends /* @vue-ignore */ Omit<MenuProps, "model"> {
    model?: SidebarMenuItem[];
  }

  const props = defineProps<SidebarMenuProps>();

  const model = computed(() => renderModel(props.model ?? []));

  function itemBadge(item: MenuItem): string | undefined {
    const n = (item as SidebarMenuItem).badge;
    if (typeof n !== "number" || n <= 0) return undefined;
    return n > 99 ? "99+" : String(n);
  }

  function renderModel(input: SidebarMenuItem[]): MenuItem[] {
    return input
      .filter((item) => {
        const { auth } = item;
        if (!auth) return true;
        return hasAccess(viewer.value, auth);
      })
      .map((item) => ({
        ...item,
        command: item.command ?? (() => navigateTo(item.url)),
        items: item.items ? renderModel(item.items) : undefined,
      }));
  }
</script>

<style lang="scss">
</style>
